/**
 * OAuth2 Authorization Modal Component
 *
 * Modal that manages the OAuth2 authorization flow UI.
 * Displays loading and error states during OAuth2 redirect flow.
 */

import { Modal, ModalProps, Stack, Text, Loader, Alert } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useRef, useCallback } from "react";
import { useOAuth2RedirectFlow } from "../../hooks/useOAuth2RedirectFlow";

/**
 * Props for OAuth2AuthorizationModal
 */
interface OAuth2AuthorizationModalProperties extends Omit<ModalProps, "onClose"> {
  /** Whether modal is open */
  opened: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Email account ID to authorize */
  accountId: string | null;
}

/**
 * OAuth2AuthorizationModal Component
 * @param root0
 * @param root0.opened
 * @param root0.onClose
 * @param root0.accountId
 * @example
 * <OAuth2AuthorizationModal
 *   opened={opened}
 *   onClose={() => setOpened(false)}
 *   accountId={selectedAccountId}
 * />
 */
export function OAuth2AuthorizationModal({
  opened,
  onClose,
  accountId,
  ...modalProperties
}: OAuth2AuthorizationModalProperties) {
  const { isAuthorizing, startAuthorization, reset } = useOAuth2RedirectFlow();
  const hasStartedReference = useRef(false);

  /**
   * Reset when modal closes or on mount
   */
  useEffect(() => {
    if (!opened) {
      hasStartedReference.current = false;
      if (!isAuthorizing) {
        reset();
      }
    }
  }, [opened, reset, isAuthorizing]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  /**
   * Start authorization when modal opens with valid accountId
   */
  useEffect(() => {
    if (opened && accountId && !isAuthorizing && !hasStartedReference.current) {
      hasStartedReference.current = true;
      startAuthorization(accountId);
    }
  }, [opened, accountId, isAuthorizing, startAuthorization]);

  return (
    <Modal
      {...modalProperties}
      opened={opened}
      onClose={handleClose}
      title="Autorização OAuth2"
      size="md"
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <Stack align="center" gap="md" py="xl">
        <Loader size="lg" />
        <Text size="sm" c="dimmed">
          Redirecionando para autorização...
        </Text>
        <Alert icon={<IconInfoCircle size={16} />} color="primary" variant="light">
          <Text size="xs">
            Você será redirecionado para a página de autorização. Após autorizar, retornará automaticamente para esta
            página.
          </Text>
        </Alert>
      </Stack>
    </Modal>
  );
}

OAuth2AuthorizationModal.displayName = "OAuth2AuthorizationModal";
